import { readCsv, rowSlugs, subjectFor } from '@platform/schema/generation';

import type { ContentStatus } from '@platform/schema/content';
import type { CsvLine, ExistingCollections } from '@platform/schema/generation';

/**
 * The upload preview — spec 013 §11.
 *
 * The parsing is `readCsv` from the contract package, not a second reader here. What this
 * adds is the only thing the preview needs beyond the rows: **the slugs each row will
 * write**, because those decide whether a re-run upserts an existing course or creates a
 * second one, and that is the question somebody uploading a corrected file is asking.
 */

export interface PreviewRow extends CsvLine {
  courseSlug?: string;
  moduleSlug?: string;
  /**
   * What the row would be filed under if nothing better decided — GEN-22.
   *
   * A **fallback**, not a promise: `ClaudePlanner` asks the model and only falls back to
   * this. It is shown so an operator sees that a topic was not given, and what was
   * ignored, before 250 modules queue rather than after.
   */
  topic?: string;
  inferred?: boolean;
}

/** Everything `readCsv` says about the file, plus the slugs — `lines` become `rows`. */
export interface Preview extends Omit<ReturnType<typeof readCsv>, 'lines'> {
  rows: PreviewRow[];
}

export function previewCsv(text: string): Preview {
  const { lines, ...header } = readCsv(text);

  const rows = lines.map((line): PreviewRow => {
    if (line.row === undefined) return line;

    const slugs = rowSlugs(line.row);

    return {
      ...line,
      courseSlug: slugs.course,
      moduleSlug: slugs.module,
      topic: subjectFor(line.row),
      inferred: line.row.subject === undefined,
    };
  });

  return { ...header, rows };
}

export function slugsOf(rows: PreviewRow[]): string[] {
  return [
    ...new Set(
      rows.flatMap((row) =>
        row.courseSlug === undefined || row.moduleSlug === undefined
          ? []
          : [row.courseSlug, row.moduleSlug],
      ),
    ),
  ];
}

/**
 * What the upload lands on that is already there. A row upserts by slug and leaves
 * anything not DRAFT as it is, so a live module gains draft lessons and keeps its live
 * ones, and an existing draft is rewritten.
 */
export function overwrites(
  rows: PreviewRow[],
  existing: ExistingCollections['collections'],
) {
  const bySlug = new Map(existing.map((found) => [found.slug, found]));
  // Keyed by slug: two courses may each hold a module with the same title.
  const live = new Map<string, string>();
  const drafts = new Map<string, string>();
  const liveCourses = new Map<string, string>();

  for (const row of rows) {
    if (row.moduleSlug === undefined || row.courseSlug === undefined) continue;

    const module = bySlug.get(row.moduleSlug);
    const course = bySlug.get(row.courseSlug);

    if (module === undefined) {
      if (course?.status === 'PUBLISHED')
        liveCourses.set(course.slug, course.title);
      continue;
    }

    const name =
      course === undefined ? module.title : `${course.title} › ${module.title}`;
    if (module.status === 'PUBLISHED') live.set(module.slug, name);
    if (module.status === 'DRAFT') drafts.set(module.slug, name);
  }

  return {
    statusOf: (slug: string | undefined): ContentStatus | undefined =>
      slug === undefined ? undefined : bySlug.get(slug)?.status,
    live: [...live.values()],
    drafts: [...drafts.values()],
    liveCourses: [...liveCourses.values()],
  };
}
